/**
 * `<HelpdeskInboxPage>` — `/w/:slug/helpdesk`
 *
 * The support-operator inbox, rebuilt to faithfully match the storybook
 * `final/_helpdesk.tsx` `HelpdeskPane`: a four-pane shell —
 *   views rail | conversation list | Thread | ContextRail
 * each `flex h-full min-h-0`, every pane header `h-12 shrink-0 border-b`, every
 * scroll body `min-h-0 flex-1 overflow-auto`. The storybook's own Thread /
 * Composer / Bubble / ContextRail components are ported (in `./Thread`,
 * `./Composer`, `./ContextRail`) rather than reusing `ConversationDetailView`.
 *
 * Wired to the existing handoff/session backend only:
 *   GET /api/handoffs/live        (session views; ?status / ?mine)
 *   GET /api/decisions            (Decisions view)
 *   GET /api/sessions/:id/messages (transcript → ported Bubble)
 *   takeover / claim / handback / human-messages / decision rule / steers
 * Live updates ride the board SSE + per-conversation SSE.
 *
 * Everything the storybook shows with no backend (teams, SLA, tickets, orders,
 * Customer-360 commerce, Copilot, saved replies, internal notes) is omitted —
 * see `aidlc-docs/construction/human-handoff/inbox-backend-gaps.md`. "Resolve"
 * maps to hand-back (the only close path).
 */
import { useAbility, useAuth, useWorkspace } from "@cometchat-ai/auth/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "@cometchat-ai/ui-dashboard";

import { Headset } from "./../components/icons";
import { EmptyState } from "../components/empty-state";
import { sessionKeys, useSessionMessages } from "../agents/api/sessions";
import { formatApiError } from "../lib/format-error";
import {
  claimHandoff,
  createNote,
  decisionKeys,
  handbackHandoff,
  resolveSession,
  listDecisions,
  listLiveSessions,
  liveKeys,
  ruleDecision,
  sendHumanMessage,
  takeOverSession,
  type SessionStatus,
} from "../handoff/api";
import { useHandoffBoardStream, useConversationLiveSync } from "../handoff/use-session-stream";

import { ConvList } from "./ConvList";
import { ContextRail } from "./ContextRail";
import { InboxViewsRail, VIEWS, type ViewId } from "./InboxViewsRail";
import { Thread } from "./Thread";
import { contactName } from "./helpers";

export function HelpdeskInboxPage() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? "";
  const { user } = useAuth();
  const myUserId = user?.id ?? null;
  const ability = useAbility();
  const canMonitor = ability.can("monitor", "Conversation");
  const canTakeover = ability.can("take-over", "HandoffEvent");
  const canClaim = ability.can("claim", "HandoffEvent");
  const canHandback = ability.can("handback", "HandoffEvent");
  const canResolve = ability.can("resolve", "HandoffEvent");
  const canNote = ability.can("note", "HandoffEvent");
  // Reply to ANY chat, not just your own claimed ones (desk leads / admins).
  const canReplyAll = ability.can("reply-all", "HandoffEvent");
  const canRule = ability.can("rule", "Decision");

  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("view");
  // Validate against known views so a stale URL (e.g. the removed `unresolved`)
  // falls back to the queue instead of crashing the title/label lookup.
  const view: ViewId = VIEWS.some((v) => v.id === rawView) ? (rawView as ViewId) : "queue";
  const selectedId = searchParams.get("c") ?? undefined;
  // My chats sub-filter: "all" (claimed/handled by me) vs "new" (auto-assigned,
  // not yet started). Auto-assign isn't wired yet, so "new" is an empty pill.
  const [minePill, setMinePill] = useState<"all" | "new">("all");
  // Queue team filter (teams-routing LLD §6): "all" | "general" | a teamId.
  const [queueTeam, setQueueTeam] = useState<string>("all");

  const setView = useCallback(
    (v: ViewId) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", v);
          next.delete("c");
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const selectConversation = useCallback(
    (id: string) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("c", id);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // Switch the My chats pill and drop the open conversation so the thread
  // matches the pill's list (the prior selection may not be in the new pill).
  const selectMinePill = useCallback(
    (id: "all" | "new") => {
      setMinePill(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("c");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Jump to a view AND open a specific conversation in one navigation — used
  // after an action so e.g. claiming lands you on My chats with that chat open.
  const goTo = useCallback(
    (v: ViewId, conversationId: string) => {
      if (v === "mine") setMinePill("all");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", v);
          next.set("c", conversationId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // One "all" fetch powers Queue / All / Unresolved + their counts; My chats
  // and Decisions are their own scoped fetches.
  const allQ = useQuery({
    queryKey: liveKeys.filtered(slug, {}),
    queryFn: () => listLiveSessions({}),
    enabled: canMonitor,
    refetchInterval: 30_000,
  });
  const mineQ = useQuery({
    queryKey: liveKeys.filtered(slug, { mine: true }),
    queryFn: () => listLiveSessions({ mine: true }),
    enabled: canMonitor,
    refetchInterval: 30_000,
  });
  const decisionsQ = useQuery({
    queryKey: decisionKeys.list(slug),
    queryFn: () => listDecisions(),
    enabled: canMonitor,
    refetchInterval: 30_000,
  });
  // Resolved is excluded from the default `all` fetch, so it's its own query.
  const resolvedQ = useQuery({
    queryKey: liveKeys.filtered(slug, { status: ["resolved"] }),
    queryFn: () => listLiveSessions({ status: ["resolved"] }),
    enabled: canMonitor,
    refetchInterval: 30_000,
  });

  const queryClient = useQueryClient();
  const onBoardEvent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: liveKeys.list(slug) });
    void queryClient.invalidateQueries({ queryKey: decisionKeys.list(slug) });
  }, [queryClient, slug]);
  useHandoffBoardStream(canMonitor ? onBoardEvent : () => {});

  const allSessions = useMemo(() => allQ.data?.items ?? [], [allQ.data]);
  const mineSessions = useMemo(() => mineQ.data?.items ?? [], [mineQ.data]);
  const decisions = useMemo(() => decisionsQ.data?.items ?? [], [decisionsQ.data]);
  const resolvedSessions = useMemo(() => resolvedQ.data?.items ?? [], [resolvedQ.data]);

  const counts: Record<ViewId, number> = useMemo(
    () => ({
      ai: allSessions.filter((s) => s.status === "ai_handling").length,
      decisions: decisions.length,
      queue: allSessions.filter((s) => s.status === "queued").length,
      mine: mineSessions.length,
      all: allSessions.filter((s) => s.status === "human_handling").length,
      resolved: resolvedSessions.length,
    }),
    [allSessions, mineSessions, decisions, resolvedSessions],
  );

  const queuedSessions = useMemo(
    () => allSessions.filter((s) => s.status === "queued"),
    [allSessions],
  );
  // Distinct routing teams present in the queue — drives the filter pills.
  const queueTeams = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of queuedSessions) {
      if (s.teamId && s.teamName && !seen.has(s.teamId)) seen.set(s.teamId, s.teamName);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [queuedSessions]);

  const sessions = useMemo(() => {
    switch (view) {
      case "ai":
        return allSessions.filter((s) => s.status === "ai_handling");
      case "queue": {
        if (queueTeam === "all" || !queueTeams.some((t) => t.id === queueTeam))
          return queueTeam === "general" ? queuedSessions.filter((s) => !s.teamId) : queuedSessions;
        return queuedSessions.filter((s) => s.teamId === queueTeam);
      }
      case "all":
        return allSessions.filter((s) => s.status === "human_handling");
      case "mine":
        // "All" is the superset — every conversation assigned to me. "New" is
        // the subset I haven't replied to yet; a reply drops it from New but it
        // stays in All.
        return minePill === "new" ? mineSessions.filter((s) => s.awaitingReply) : mineSessions;
      case "resolved":
        return resolvedSessions;
      default:
        return [];
    }
  }, [
    view,
    minePill,
    queueTeam,
    queueTeams,
    queuedSessions,
    allSessions,
    mineSessions,
    resolvedSessions,
  ]);

  const listQ =
    view === "mine"
      ? mineQ
      : view === "decisions"
        ? decisionsQ
        : view === "resolved"
          ? resolvedQ
          : allQ;

  // Every conversation/decision id we currently know about, across all fetches.
  // A post-action `goTo` target (e.g. a just-claimed chat) is always "known"
  // via `allSessions`, even before it lands in the destination view's own list.
  const knownIds = useMemo(() => {
    const s = new Set<string>();
    for (const x of allSessions) s.add(x.id);
    for (const x of mineSessions) s.add(x.id);
    for (const x of resolvedSessions) s.add(x.id);
    for (const d of decisions) s.add(d.id);
    return s;
  }, [allSessions, mineSessions, resolvedSessions, decisions]);

  // Auto-open the first row when nothing is selected, or when the selection is
  // truly stale (no longer a known conversation). A freshly navigated-to id
  // that's still loading into THIS view's list is left untouched, so post-action
  // navigation ("claim → My chats → that chat open") doesn't get overridden.
  const rowIds = view === "decisions" ? decisions.map((d) => d.id) : sessions.map((s) => s.id);
  useEffect(() => {
    if (rowIds.length === 0) return;
    if (!selectedId || !knownIds.has(selectedId)) selectConversation(rowIds[0]!);
  }, [rowIds, knownIds, selectedId, selectConversation]);

  const selectedDecision =
    view === "decisions" ? decisions.find((d) => d.id === selectedId) : undefined;
  const selectedSession = useMemo(
    () =>
      view === "decisions"
        ? undefined
        : [...allSessions, ...mineSessions, ...resolvedSessions].find((s) => s.id === selectedId),
    [view, allSessions, mineSessions, resolvedSessions, selectedId],
  );
  // The session behind a decision (for its channel / status), if it's live.
  const decisionSession = useMemo(
    () =>
      selectedDecision
        ? [...allSessions, ...mineSessions].find((s) => s.id === selectedDecision.sessionId)
        : undefined,
    [selectedDecision, allSessions, mineSessions],
  );

  const activeSessionId = view === "decisions" ? selectedDecision?.sessionId : selectedId;
  const activeHandoffId = selectedSession?.activeHandoffId ?? null;
  // Ownership gate: owner-only actions (public reply, hand back, resolve) apply
  // ONLY when THIS conversation is claimed by the current user. A teammate with
  // the RBAC ability still can't act on a chat another agent owns — they can
  // read it and add an internal note (collaboration). Takeover/claim are
  // unaffected (they act on ai_handling/queued, not a claimed chat).
  const isOwnedByMe = !!myUserId && selectedSession?.claimedByUserId === myUserId;
  const ownerName = selectedSession?.claimedBy?.displayName ?? null;

  // Transcript — load the full thread (auto-page) so the newest bubbles land
  // at the bottom, then rehydrate live via the per-conversation SSE.
  const msgQ = useSessionMessages(activeSessionId);
  useEffect(() => {
    if (msgQ.hasNextPage && !msgQ.isFetchingNextPage) void msgQ.fetchNextPage();
  }, [msgQ.hasNextPage, msgQ.isFetchingNextPage, msgQ.data, msgQ]);
  const messages = useMemo(() => msgQ.data?.pages.flatMap((p) => p.items) ?? [], [msgQ.data]);
  useConversationLiveSync(activeSessionId ?? "");

  const invalidateLive = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: liveKeys.list(slug) });
    if (activeSessionId)
      void queryClient.invalidateQueries({ queryKey: sessionKeys.messages(activeSessionId) });
  }, [queryClient, slug, activeSessionId]);

  // After an action, land the operator where the conversation now lives (with
  // it open): take-over/claim → My chats, hand-back → AI Agents, resolve →
  // Resolved. `activeSessionId` is captured here (the row that was acted on).
  const takeover = useMutation({
    mutationFn: () => takeOverSession(activeSessionId!),
    onSuccess: () => {
      const sid = activeSessionId;
      toast.success(t("handoff.tookOverToast"));
      invalidateLive();
      if (sid) goTo("mine", sid);
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const claim = useMutation({
    mutationFn: () => claimHandoff(activeHandoffId!),
    onSuccess: () => {
      const sid = activeSessionId;
      toast.success(t("handoff.claimedToast"));
      invalidateLive();
      if (sid) goTo("mine", sid);
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const handback = useMutation({
    mutationFn: () => handbackHandoff(activeHandoffId!),
    onSuccess: () => {
      const sid = activeSessionId;
      toast.success(t("handoff.handedBackToast"));
      invalidateLive();
      if (sid) goTo("ai", sid);
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const resolve = useMutation({
    // Session-keyed direct resolve: works in every open status without
    // claiming/taking over first (no ownership, no join announcement).
    mutationFn: () => resolveSession(activeSessionId!),
    onSuccess: () => {
      const sid = activeSessionId;
      toast.success(t("handoff.resolvedToast"));
      invalidateLive();
      if (sid) goTo("resolved", sid);
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const send = useMutation({
    mutationFn: (text: string) => sendHumanMessage(activeSessionId!, text),
    onSuccess: () => {
      if (activeSessionId)
        void queryClient.invalidateQueries({ queryKey: sessionKeys.messages(activeSessionId) });
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const note = useMutation({
    mutationFn: (text: string) => createNote(activeSessionId!, text),
    onSuccess: () => {
      toast.success(t("helpdesk.composer.noteSentToast"));
      if (activeSessionId)
        void queryClient.invalidateQueries({ queryKey: sessionKeys.messages(activeSessionId) });
    },
    onError: (e) => toast.error(t("handoff.actionFailed"), { description: formatApiError(e) }),
  });
  const rule = useMutation({
    mutationFn: (v: { outcome: "accepted" | "denied"; note?: string }) =>
      ruleDecision(selectedDecision!.id, v.outcome, v.note),
    onSuccess: (_r, v) => {
      toast.success(
        t(v.outcome === "accepted" ? "decisions.approvedToast" : "decisions.deniedToast"),
      );
      void queryClient.invalidateQueries({ queryKey: decisionKeys.list(slug) });
    },
    onError: (e) => toast.error(t("decisions.failed"), { description: formatApiError(e) }),
  });

  if (!canMonitor) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState icon={Headset} title={t("helpdesk.forbidden")} />
      </div>
    );
  }

  const hasSelection = view === "decisions" ? !!selectedDecision : !!selectedSession;
  const headerName =
    view === "decisions"
      ? contactName(selectedDecision?.contact ?? null, selectedDecision?.subjectKey)
      : contactName(selectedSession?.contact ?? null, selectedSession?.title);
  const headerEmail =
    view === "decisions"
      ? (selectedDecision?.contact?.email ?? null)
      : (selectedSession?.contact?.email ?? null);
  const headerChannel = selectedSession?.channel ?? decisionSession?.channel ?? "web";
  const headerStatus: SessionStatus =
    selectedSession?.status ?? decisionSession?.status ?? "ai_handling";
  // Prefer the handoff reason; else the title — but not when the title is
  // already serving as the header name (anonymous contact), to avoid "Cas / Cas".
  const headerSubject =
    selectedSession?.reason ??
    (headerName === selectedSession?.title ? null : (selectedSession?.title ?? null));

  return (
    <div className="flex h-full min-h-0">
      <InboxViewsRail view={view} counts={counts} onSelect={setView} />

      <ConvList
        title={t(VIEWS.find((v) => v.id === view)!.labelKey)}
        view={view}
        sessions={sessions}
        decisions={decisions}
        selectedId={selectedId}
        onSelect={selectConversation}
        isPending={listQ.isPending}
        isError={listQ.isError}
        error={listQ.error}
        onRetry={listQ.refetch}
        emptyLabel={
          view === "mine" && minePill === "new" ? t("helpdesk.mineNewEmpty") : t("helpdesk.empty")
        }
        pills={
          view === "mine"
            ? [
                { id: "all", label: t("helpdesk.minePills.all"), count: mineSessions.length },
                {
                  id: "new",
                  label: t("helpdesk.minePills.new"),
                  count: mineSessions.filter((s) => s.awaitingReply).length,
                },
              ]
            : view === "queue" && queueTeams.length > 0
              ? [
                  {
                    id: "all",
                    label: t("helpdesk.queuePills.all"),
                    count: queuedSessions.length,
                  },
                  ...(queuedSessions.some((s) => !s.teamId)
                    ? [
                        {
                          id: "general",
                          label: t("helpdesk.queuePills.general"),
                          count: queuedSessions.filter((s) => !s.teamId).length,
                        },
                      ]
                    : []),
                  ...queueTeams.map((tm) => ({
                    id: tm.id,
                    label: tm.name,
                    count: queuedSessions.filter((s) => s.teamId === tm.id).length,
                  })),
                ]
              : undefined
        }
        activePill={view === "queue" ? queueTeam : minePill}
        onPillSelect={(id) =>
          view === "queue" ? setQueueTeam(id) : selectMinePill(id as "all" | "new")
        }
      />

      {!hasSelection ? (
        <div className="flex min-h-0 flex-[2] items-center justify-center text-2sm text-muted-foreground">
          {t("helpdesk.selectPrompt")}
        </div>
      ) : (
        <Thread
          name={headerName}
          subject={headerSubject}
          channel={headerChannel}
          status={headerStatus}
          view={view}
          messages={messages}
          isLoading={!!activeSessionId && msgQ.isPending}
          isError={msgQ.isError}
          onRetry={() => void msgQ.refetch()}
          sending={send.isPending}
          sendingNote={note.isPending}
          onSend={(text) => send.mutate(text)}
          onSendNote={(text) => note.mutate(text)}
          onTakeover={() => takeover.mutate()}
          onHandback={() => handback.mutate()}
          onResolve={() => resolve.mutate()}
          canResolve={canResolve && !!activeSessionId}
          onClaim={() => claim.mutate()}
          canTakeover={canTakeover && !!activeSessionId}
          canHandback={canHandback && !!activeHandoffId && isOwnedByMe}
          canClaim={canClaim && !!activeHandoffId}
          canNote={canNote && !!activeSessionId}
          isOwnedByMe={isOwnedByMe}
          canReplyAll={canReplyAll}
          ownerName={ownerName}
          decision={selectedDecision}
          deciding={rule.isPending}
          onApprove={(note) => rule.mutate({ outcome: "accepted", note })}
          onDeny={(note) => rule.mutate({ outcome: "denied", note })}
          canRule={canRule}
        />
      )}

      {hasSelection && activeSessionId ? (
        <ContextRail
          sessionId={activeSessionId}
          contactId={selectedSession?.contactId ?? decisionSession?.contactId ?? null}
          name={headerName}
          email={headerEmail}
          channel={headerChannel}
          slug={slug}
        />
      ) : null}
    </div>
  );
}
